import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CTASection from '@/pages/home/components/CTASection';
import { categories, articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, BreadcrumbListSchema, CollectionPageSchema } from '@/components/feature/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const canonicalUrl = typeof window !== 'undefined' ? 'http://sxthpxzx.cn' : '';
  const pageUrl = `${canonicalUrl}/category/${slug}`;

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const filteredCategoryArticles = articles.filter((a) => a.category === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        url={pageUrl}
      />
      <CollectionPageSchema
        name={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        url={pageUrl}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: canonicalUrl },
          { name: category.name, url: pageUrl },
        ]}
      />
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="w-full bg-background-50 pt-20 pb-4">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 font-medium">{category.name}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Bold%20abstract%20warm%20gradient%20composition%20with%20orange%20coral%20and%20amber%20tones%2C%20dynamic%20geometric%20shapes%2C%20cinematic%20lighting%2C%20modern%20editorial%20design%2C%20clean%20background%20with%20vibrant%20accents%2C%20professional%20news%20portal%20aesthetic&width=1800&height=700&seq=category-hero-generic&orientation=landscape"
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col lg:flex-row items-start gap-8">
                <div className="w-full lg:w-5/12 pt-8">
                  <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
                    {category.name}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed max-w-md">
                    {category.description}
                  </p>
                  <div className="mt-5 text-xs text-background-400">
                    共 {categoryArticles.length} 篇爆料文章
                  </div>
                </div>
                <div className="w-full lg:w-7/12">
                  {categoryArticles.length > 0 && (
                    <div className="relative rounded-2xl overflow-hidden">
                      <div className="aspect-[16/10]">
                        <img
                          src={categoryArticles[0].image}
                          alt={categoryArticles[0].title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h2 className="text-lg md:text-xl font-heading font-bold text-background-50 line-clamp-2">
                          {categoryArticles[0].title}
                        </h2>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        {article.isHot && (
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                            热门
                          </span>
                        )}
                      </div>
                      <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-100">
                        <span className="text-xs text-foreground-500">{article.date}</span>
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {(article.views / 10000).toFixed(1)}万
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line"></i>
                            {article.comments}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}